import { useEffect, useState } from "react";
import { motion } from "framer-motion";

const floors = [
  { id: "floor-00", label: "00" },
  { id: "floor-01", label: "01" },
  { id: "floor-02", label: "02" },
  { id: "floor-03", label: "03" },
  { id: "floor-04", label: "04" },
  { id: "floor-05", label: "05" },
  { id: "floor-06", label: "06" },
  { id: "floor-07", label: "07" },
  { id: "floor-08", label: "08" },
];

const FloorIndicator = () => {
  const [currentFloor, setCurrentFloor] = useState(0);

  useEffect(() => {
    // Defer observer setup so it doesn't compete with first paint.
    const ric =
      (window as any).requestIdleCallback ||
      ((cb: () => void) => setTimeout(cb, 600));

    let observer: IntersectionObserver | null = null;
    // Track which floors are currently crossing a horizontal line near the top
    // of the viewport. The top-most intersecting one becomes the active floor.
    const visible = new Set<number>();

    const handle = ric(() => {
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const idx = floors.findIndex((f) => f.id === entry.target.id);
            if (idx === -1) return;
            if (entry.isIntersecting) visible.add(idx);
            else visible.delete(idx);
          });
          if (visible.size > 0) {
            // Highest index currently crossing the trigger line = furthest scrolled.
            const next = Math.max(...visible);
            setCurrentFloor(next);
          }
        },
        // Trigger line ~35% from top of viewport. Works for tall and short sections.
        { rootMargin: "-35% 0px -65% 0px", threshold: 0 }
      );
      floors.forEach((f) => {
        const el = document.getElementById(f.id);
        if (el && observer) observer.observe(el);
      });
    });

    return () => {
      observer?.disconnect();
      if ((window as any).cancelIdleCallback && typeof handle === "number") {
        (window as any).cancelIdleCallback(handle);
      }
    };
  }, []);

  // Track fill is derived from the current floor (which already updates via
  // IntersectionObserver). This avoids a global useScroll listener that
  // forces layout reads on every scroll tick.
  const fillPct = ((currentFloor + 1) / floors.length) * 100;

  return (
    <div className="fixed right-6 top-1/2 -translate-y-1/2 z-40 hidden lg:flex flex-col items-center gap-0">
      {/* Track line */}
      <div className="relative w-px h-48" style={{ background: "hsl(0 0% 100% / 0.06)" }}>
        <div
          className="absolute top-0 left-0 w-full bg-primary transition-[height] duration-500 ease-out"
          style={{ height: `${fillPct}%` }}
        />
      </div>

      {/* Floor number */}
      <div className="mt-4 text-center">
        <motion.p
          key={currentFloor}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          className="font-body text-[10px] tracking-[0.3em] text-primary/70 font-semibold"
        >
          {floors[currentFloor]?.label}
        </motion.p>
        <p className="text-[8px] tracking-[0.2em] uppercase text-muted-foreground/40 mt-1">Floor</p>
      </div>
    </div>
  );
};

export default FloorIndicator;
